import { Component, h, Prop, Element, Method } from '@stencil/core';
import Chart, {
  ChartOptions,
  PluginServiceRegistrationOptions,
  ChartData,
  ChartType,
} from 'chart.js';
import merge from 'lodash.merge';
import { Maybe } from '../../../global/types';
import { getDefaultOptions } from './chart.options';

export interface Dimension {
  width: string;
  height: string;
}

@Component({
  tag: 'rt-graph',
  shadow: true,
  styleUrl: 'graph.scss',
})
export class GraphComponent {
  chart: Chart;

  @Element() element: HTMLElement;

  elementFontFamily = '';

  @Prop() type: ChartType;

  @Prop() options: Maybe<ChartOptions>;

  @Prop() data: ChartData;

  @Prop() plugins: PluginServiceRegistrationOptions[] = [];

  @Prop() size: Maybe<Partial<Dimension>>;

  @Method()
  async updateChartData(data: ChartData) {
    if (this.chart) {
      this.chart.data.datasets.forEach((dataset, index) => {
        dataset.data = data.datasets[index].data;
      });
      this.chart.update();
    }
  }

  @Method()
  async updateChartOptions(options: Partial<ChartOptions>) {
    if (this.chart) {
      this.chart.options = merge(
        getDefaultOptions(
          this.elementFontFamily,
          this.element.shadowRoot.querySelector('.chartjs-tooltip'),
        ),
        options,
      );
      this.chart.update();
    }
  }

  componentDidLoad() {
    this.elementFontFamily = window.getComputedStyle(
      this.element,
      null,
    ).fontFamily;
    const canvas = this.element.shadowRoot.querySelector('canvas');
    if (canvas) {
      const ctx = canvas.getContext('2d');
      this.chart = new Chart(ctx, {
        type: this.type,
        data: this.data,
        options: merge(
          getDefaultOptions(
            this.elementFontFamily,
            this.element.shadowRoot.querySelector('.chartjs-tooltip'),
          ),
          this.options,
        ),
        plugins: this.plugins,
      });
    }
  }

  @Method()
  async getSnapshot(
    config?: Partial<{
      size: Partial<Dimension>;
      mimeType: string;
      encoderOptions: number;
    }>,
  ): Promise<string> {
    const {
      size = { width: '650px' },
      mimeType = 'image/png',
      encoderOptions = 0.92,
    } = config;
    const canvas = this.element.shadowRoot.querySelector('canvas');
    const chartContainer = this.element.shadowRoot.querySelector(
      '.chart-container',
    ) as HTMLElement;
    if (!canvas || !chartContainer) {
      return '';
    }

    if (size.width) {
      chartContainer.style.width = size.width;
    }
    if (size.height) {
      chartContainer.style.height = size.height;
    }
    this.chart.resize();
    const dataUrl = canvas.toDataURL(mimeType, encoderOptions);
    chartContainer.style.width = '';
    chartContainer.style.height = '';
    this.chart.resize();
    return dataUrl;
  }

  handleDblClick = () => {
    const tooltip: HTMLElement = this.element.shadowRoot.querySelector(
      '.chartjs-tooltip',
    );
    if (tooltip) {
      tooltip.style.opacity = '0';
    }
  };

  render() {
    return (
      <div
        onDblClick={this.handleDblClick}
        class="chart-container"
        style={{
          height: '350px',
          ...this.size,
        }}
      >
        <canvas></canvas>
        <div class="chartjs-tooltip"></div>
      </div>
    );
  }

  disconnectedCallback() {
    if (typeof this.chart?.destroy === 'function') {
      this.chart.destroy();
    }
  }
}
